/**
 * ICONE — glifi SVG del design system (nuovo look, port dal mockup).
 *
 * Sono icone disegnate a mano (stroke, `currentColor`): il colore lo eredita dal
 * testo del contenitore, la dimensione la dà il CSS (`width/height` via classe,
 * es. `.rn-icon svg`, `.btn-primary svg`). NON sono icone `@mui/icons-material`:
 * il progetto usa MUI dove serve un componente ricco, ma per lo chrome (topbar,
 * rail, liste, bacheca) servono i glifi esatti del mockup.
 *
 * Uso:
 *   <SearchGlyph />                    // inline, dimensionato dal CSS del padre
 *
 * La risoluzione per stringa delle icone di navigazione (voci di menu/plugin)
 * vive in `Sidebar.tsx` (`navIcon`), che è il solo consumer: qui restano i soli
 * glifi, così il file esporta unicamente componenti (fast-refresh pulito).
 *
 * Aggiungere un glifo = una funzione qui. Nessun altro punto da toccare.
 */

import type { SVGProps } from 'react';

// Attributi comuni: viewBox 24, contorno su `currentColor`, giunzioni morbide.
// Lo spessore di default è 2; i glifi che ne vogliono un altro lo sovrascrivono.
const BASE: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

// ── Glifi UI (chrome, liste, bacheca) ──────────────────────────────────────
export function SearchGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" /></svg>);
}
export function CheckGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} strokeWidth={3} {...p}><path d="M5 12.5l4 4L19 7" /></svg>);
}
export function TrashGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>);
}
export function EditGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M5 19h14" /><path d="M14 4.5l4 4-9 9-4 1 1-4z" /></svg>);
}
export function PlusGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M12 5v14M5 12h14" /></svg>);
}
export function QueuesGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>);
}
export function ImportGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M12 3v12M8 11l4 4 4-4M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" /></svg>);
}
// Arco quasi chiuso più la punta di freccia: una sola freccia e non due, perché
// a 15px il doppio arco si impasta e si legge come un cerchio qualsiasi.
export function RefreshGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M20 12a8 8 0 1 1-2.4-5.7" /><path d="M20 4v4.2h-4.2" /></svg>);
}
export function TableGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M3 14h18M9 4v16" /></svg>);
}
export function BoardGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3" y="4" width="5" height="16" rx="1.5" /><rect x="10" y="4" width="5" height="11" rx="1.5" /><rect x="17" y="4" width="4" height="14" rx="1.5" /></svg>);
}
export function ClockGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>);
}
export function CalendarGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3.5" y="5" width="17" height="16" rx="2.5" /><path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" /></svg>);
}
export function FlagGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M5 21V4M5 4h11l-1.5 3.5L16 11H5" /></svg>);
}
export function TagGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} strokeLinecap="butt" {...p}><path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9z" /><circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" /></svg>);
}
export function ChevronDownGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="m6 9 6 6 6-6" /></svg>);
}
export function LogoutGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M15 12H4M9 7l-5 5 5 5M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /></svg>);
}

// ── Glifi di navigazione (rail + gear condiviso con la topbar) ──────────────
export function DashboardGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" /></svg>);
}
export function ContactsGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.6 3-6 7-6s7 2.4 7 6" /></svg>);
}
export function CompaniesGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M3 21h18" /><path d="M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16" /><path d="M14 10h4a1 1 0 0 1 1 1v10" /><path d="M9 8h1.5M9 12h1.5M9 16h1.5" /></svg>);
}
export function QuotationsGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M6 2.5h8l4 4V21l-2-1.3L14 21l-2-1.3L10 21l-2-1.3L6 21z" /><path d="M9 8.5h6M9 12h6M9 15.5h4" /></svg>);
}
export function InboxGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 5.5L20 7" /></svg>);
}
export function MailGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7 8 6 8-6" /></svg>);
}
export function PhoneGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M6 3.5c.5 0 1 .3 1.2.8l1 2.4c.2.5.1 1-.2 1.4l-1 1.1a12 12 0 0 0 5 5l1.1-1c.4-.3.9-.4 1.4-.2l2.4 1c.5.2.8.7.8 1.2v2.6c0 1-.8 1.8-1.8 1.7A15.5 15.5 0 0 1 4 6.3 1.7 1.7 0 0 1 5.7 4.5" /></svg>);
}
export function AutomationsGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20.5 3.5v4h-4" /></svg>);
}
export function FairsGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M4 10.5V20h16v-9.5" /><path d="M3 6h18l-1.4 3.6a2.8 2.8 0 0 1-5.2 0 2.8 2.8 0 0 1-5.6 0 2.8 2.8 0 0 1-5.2 0z" /><path d="M10 20v-4.5h4V20" /></svg>);
}
export function SettingsGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><circle cx="12" cy="12" r="3.2" /><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" /></svg>);
}
// Il fascicolo: due fogli sovrapposti. Il mockup dava alle Pratiche un foglio
// singolo, ma accanto a QuotationsGlyph — che è anch'esso un foglio con delle
// righe — a 20px le due voci diventavano la stessa macchia: lo zigzag che
// distingue la ricevuta è un dettaglio da tre pixel. La pila cambia la SAGOMA,
// che è l'unica cosa che sopravvive alla dimensione del rail, e dice quello che
// una pratica è: più documenti raccolti in uno.
//
// Non una cartelletta: quella è quasi identica a FolderGlyph, il glifo su cui
// `navIcon` ripiega quando un token non esiste — una voce buona finirebbe per
// somigliare a una voce rotta.
export function DossierGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M5.5 17.5A2.5 2.5 0 0 1 3 15V5.5A2.5 2.5 0 0 1 5.5 3h9A2.5 2.5 0 0 1 17 5.5v1" /><rect x="8" y="6.5" width="12.5" height="14.5" rx="2.5" /><path d="M11.5 11.5h5.5M11.5 15.5h5.5" /></svg>);
}
// L'occhio della Sorveglianza. Stesso glifo del mockup: quella voce si guarda,
// non si opera.
export function EyeGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3.2" /></svg>);
}
// Il segnaposto delle Visite. Stesso glifo del prototipo F2: la visita è un
// luogo dove l'agente va.
export function VisitGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M20 10c0 5.2-8 12-8 12s-8-6.8-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="2.8" /></svg>);
}
// La Mappa: la cartina piegata in tre, perché il segnaposto è già delle Visite.
export function MapGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M3 6.5l6-2.5 6 2.5 6-2.5v13.5l-6 2.5-6-2.5-6 2.5z" /><path d="M9 4v13.5M15 6.5V20" /></svg>);
}
export function FolderGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M4 7a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" /></svg>);
}
// KPI dashboard.
export function TargetGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} strokeLinecap="butt" {...p}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.4" /></svg>);
}
export function StarGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M4 8l3.5 3L12 5l4.5 6L20 8l-1.5 10h-13z" /></svg>);
}
export function CheckSquareGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><rect x="3.5" y="3.5" width="17" height="17" rx="5" /><path d="M8 12l2.5 2.5L16 9" /></svg>);
}
// ── Glifi delle azioni sulle automazioni ───────────────────────────────────
// Tracciati presi dal concept "Visualizza template": la pila di fogli per una
// nuova versione, i due segni del pause per la sospensione, il triangolo del
// play per la simulazione.
export function LayersGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} strokeLinecap="butt" {...p}><path d="m12 3 9 5-9 5-9-5z" /><path d="m3 13 9 5 9-5" /></svg>);
}
export function PauseGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} strokeWidth={1.9} {...p}><path d="M9 5v14M15 5v14" /></svg>);
}
export function PlayGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} strokeWidth={1.9} strokeLinecap="butt" {...p}><path d="M7 5l12 7-12 7z" /></svg>);
}
// Chiudi / scarta: usata per "Ignora Richiesta", che mette da parte senza
// cancellare — per quello c'e' TrashGlyph, e sarebbe una promessa sbagliata.
export function CloseGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M6 6l12 12M18 6L6 18" /></svg>);
}
// Collegamento fra due record (catena).
export function LinkGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><path d="M10 13.5a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7l-1.4 1.4" /><path d="M14 10.5a4 4 0 0 0-5.7 0l-2.8 2.8a4 4 0 0 0 5.7 5.7l1.4-1.4" /></svg>);
}
export function AlertGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5M12 16h.01" /></svg>);
}
// Informazione: la "i" nel cerchio. Stesso cerchio di AlertGlyph con punto e
// asta invertiti — sono la coppia "attenzione" / "informazione" e devono
// leggersi come varianti della stessa forma.
export function InfoGlyph(p: SVGProps<SVGSVGElement>) {
  return (<svg {...BASE} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11.5v5M12 8h.01" /></svg>);
}
